<script setup lang="ts">
import {computed, inject} from 'vue'
import {useLocalization} from '@hantera/portal-app'
import PermissionPicker from '../Shared/PermissionPicker.vue'
import type {useClientStore} from '@/stores/iam/client-store'
import {ToggleSwitch} from '@hantera/design-system'

const t = useLocalization('iam.clients')
const store = inject<ReturnType<typeof useClientStore>>('clientStore')!

const client = computed(() => store.client)
const canWrite = computed(() => store.canWrite)

const displayClientCredentials = computed({
  get: () => {
    const pending = store.pendingClientCredentials
    if (pending !== null) return pending
    return client.value?.grantTypes?.includes('client_credentials') ?? false
  },
  set: (value: boolean) => {
    store.pendingClientCredentials = value
  },
})

const displayRoles = computed({
  get: () => store.pendingRoleIds ?? client.value?.roles ?? [],
  set: (value: string[]) => {
    store.pendingRoleIds = value
  },
})

const inheritedPermissions = computed(() => {
  if (!client.value) return []
  return []
})

const displayPermissions = computed({
  get: () => store.pendingAcl ?? client.value?.acl ?? [],
  set: (value: string[]) => {
    store.pendingAcl = value
  },
})

const enableClientCredentials = computed(() => displayClientCredentials.value)

function toggleRole(roleKey: string) {
  const current = displayRoles.value
  const index = current.indexOf(roleKey)
  if (index > -1) {
    displayRoles.value = current.filter(r => r !== roleKey)
  } else {
    displayRoles.value = [...current, roleKey]
  }
}

const availableRoles = computed(() => store.roles)
</script>

<template>
  <div v-if="client" class="service-account-tab">
    <div class="content">
      <div class="tab-section">
        <label class="enable-checkbox">
          <ToggleSwitch
            type="checkbox" 
            v-model="displayClientCredentials"
            :disabled="!canWrite"
          />
          <span class="checkbox-label">{{ t('serviceAccount.enableLabel') }}</span>
        </label>
        <p class="help-text">{{ t('serviceAccount.helpText') }}</p>
      </div>
  
      <div class="tab-section" :class="{ disabled: !enableClientCredentials }">
        <h3>{{ t('serviceAccount.roles') }}</h3>
        
        <div class="role-checkboxes">
          <label v-for="role in availableRoles" :key="role.key">
            <input 
              type="checkbox" 
              :checked="displayRoles.includes(role.key)"
              @change="toggleRole(role.key)"
              :disabled="!canWrite || !enableClientCredentials"
            />
            {{ role.key }}
            <span v-if="role.description" class="role-description">— {{ role.description }}</span>
          </label>
        </div>
      </div>
  
      <div class="tab-section" :class="{ disabled: !enableClientCredentials }">
        <h3>{{ t('serviceAccount.permissions') }}</h3>
        
        <PermissionPicker
          v-if="client"
          v-model="displayPermissions"
          :inherited-permissions="inheritedPermissions"
          :readonly="!canWrite || !enableClientCredentials"
        />
      </div>
    </div>

    <div v-if="store.hasServiceAccountChanges" class="action-bar">
      <button @click="store.saveServiceAccount" class="btn-primary">{{ t('actions.saveChanges') }}</button>
      <button @click="store.discardServiceAccount" class="btn-silent">{{ t('actions.discard') }}</button>
    </div>
  </div>

  
</template>

<style scoped>
.service-account-tab {
  display: grid;
  grid-template-rows: auto min-content;
  min-height: 100%;
}

.content {
  display: flex;
  flex-direction: column;
}

.tab-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
}

.tab-section.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.enable-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  cursor: pointer;
}

.enable-checkbox input[type="checkbox"] {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
}

.checkbox-label {
  font-size: 1rem;
}

.help-text {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  margin: 0;
}

h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.role-checkboxes {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--color-surface);
  border-radius: 4px;
  max-height: 400px;
  overflow-y: auto;
}

.role-checkboxes label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.role-description {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
}

.edit-actions {
  display: flex;
  gap: 0.5rem;
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: var(--color-primary-bg);
  color: var(--color-primary);
  border-radius: 4px;
  font-size: 0.875rem;
}

.action-bar {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid hsl(var(--color-80));
  background: hsl(var(--color-90));
  position: sticky;
  bottom: 0;
  box-shadow: 0 0px 20px var(--color-shadow);
}
</style>
